/**
 * Guest session API (SCRUM-273/274) — the /auth/guest/* surface from the
 * shared contract (docs/superpowers/plans/2026-10-07-guest-chat.md).
 *
 * The session is created lazily — on the first send or first mic use — by
 * `ensureGuestSession`, never on page load: a visitor who only reads costs
 * nothing (bots included). Creation and refresh run under the cross-tab guest
 * session lock so two tabs never mint two guests or replay one refresh token.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useChatStore } from "@/features/chat/chat-store";
import {
  isGuestErrorCode,
  parseGuestAllowance,
  parseGuestRefusal,
  type GuestAllowance,
  type GuestErrorCode,
} from "@/features/chat/guest/allowance";
import {
  clearGuestDraft,
  clearGuestPointer,
  readGuestPointer,
  updateGuestPointer,
} from "@/features/chat/guest/pointer";
import { withGuestSessionLock } from "@/features/chat/guest/session-lock";
import {
  AUTH_PATHS,
  ApiError,
  apiFetch,
  attemptGuestRefresh,
  attemptRefresh,
  type RefreshOutcome,
} from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { queryClient } from "@/lib/queryClient";

/** GET /auth/guest/config (public). */
export interface GuestConfig {
  enabled: boolean;
  soft_mode: boolean;
  max_messages: number;
  warn_at_remaining: number;
  retention_days: number;
  voice_enabled: boolean;
  attachments_enabled: boolean;
  /** Contract amendment C2 — what GET /chat/voice/config would report, for
   * the microphone before a guest session (and token) exists. */
  voice_languages: string[];
  voice_max_duration_seconds: number;
}

/** POST /auth/guest and POST /auth/guest/refresh. */
export interface GuestSessionResponse {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  allowance: GuestAllowance | null;
}

/** GET /auth/guest/me. */
export interface GuestMe {
  allowance: GuestAllowance | null;
  /**
   * `null` when the body was empty or the field was not an array — unknown,
   * NOT the same as a real empty list. `syncPointerFromMe` must never read
   * `null` as "the server says zero threads" (fix round 1, IMPORTANT: a
   * malformed 200 was silently dropping the guest's thread).
   */
  thread_ids: string[] | null;
}

/** POST /auth/guest/claim. */
export interface GuestClaimResult {
  thread_ids: string[];
  active_thread_id: string | null;
}

/**
 * A guest session could not be created: a contract code, `"network"`, or
 * `"already-member"` — a client-only guard (fix round 1, item 4): a signed-in
 * tab must never mint a shadow guest, so `ensureGuestSession` rejects with
 * this BEFORE any request when a member access token is already in memory.
 * The server is never asked and never produces this code itself.
 */
export class GuestSessionError extends Error {
  readonly code: GuestErrorCode | "network" | "already-member";
  readonly retryAfterSeconds: number | null;

  constructor(
    code: GuestErrorCode | "network" | "already-member",
    retryAfterSeconds: number | null,
  ) {
    super(code);
    this.name = "GuestSessionError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** The guest code on an `ApiError` (top-level or nested — C4), else null. */
export function guestErrorCodeOf(err: unknown): GuestErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  const code = err.code;
  return code !== undefined && isGuestErrorCode(code) ? code : null;
}

function normaliseGuestConfig(raw: Partial<GuestConfig> | undefined): GuestConfig {
  // A field the server omits reads as its safe value: off, hard wall, no extras
  // (no voice languages = no microphone before a session).
  const languages: unknown = raw?.voice_languages;
  return {
    enabled: raw?.enabled === true,
    soft_mode: raw?.soft_mode === true,
    max_messages: typeof raw?.max_messages === "number" ? raw.max_messages : 10,
    warn_at_remaining:
      typeof raw?.warn_at_remaining === "number" ? raw.warn_at_remaining : 0,
    retention_days: typeof raw?.retention_days === "number" ? raw.retention_days : 7,
    voice_enabled: raw?.voice_enabled === true,
    attachments_enabled: raw?.attachments_enabled === true,
    voice_languages: Array.isArray(languages)
      ? languages.filter((lang): lang is string => typeof lang === "string")
      : [],
    voice_max_duration_seconds:
      typeof raw?.voice_max_duration_seconds === "number"
        ? raw.voice_max_duration_seconds
        : 60,
  };
}

/**
 * The fail-closed shape (fix round 1, SCRUM-275): what every field reads as
 * when the server omits it. `useGuestMode` (HomeRoute) falls back to this
 * when the config fetch itself errors but an existing guest session must not
 * lose its conversation (contract amendment C5).
 */
export const FAIL_CLOSED_GUEST_CONFIG: GuestConfig = normaliseGuestConfig(undefined);

export async function fetchGuestConfig(): Promise<GuestConfig> {
  const raw = await apiFetch<Partial<GuestConfig> | undefined>({
    method: "GET",
    path: AUTH_PATHS.guestConfig,
    skipRefreshOn401: true,
  });
  return normaliseGuestConfig(raw);
}

export const GUEST_CONFIG_QUERY_KEY = ["guest-config"] as const;

export function useGuestConfig(
  options: { enabled?: boolean } = {},
): UseQueryResult<GuestConfig, Error> {
  return useQuery<GuestConfig, Error>({
    queryKey: GUEST_CONFIG_QUERY_KEY,
    queryFn: fetchGuestConfig,
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

function toGuestSessionError(err: unknown): GuestSessionError {
  if (err instanceof GuestSessionError) return err;
  if (err instanceof ApiError) {
    const refusal = parseGuestRefusal(err.problem.detail_object ?? err.problem);
    const code = guestErrorCodeOf(err) ?? "guest.unavailable";
    return new GuestSessionError(
      code,
      err.retryAfterSeconds ?? refusal?.retryAfterSeconds ?? null,
    );
  }
  return new GuestSessionError("network", null);
}

/** Raw POST /auth/guest response shape, before validation. */
interface RawGuestSession {
  access_token?: unknown;
  expires_in?: unknown;
  allowance?: unknown;
}

/**
 * POST /auth/guest — get-or-create (a valid `garden_gst` returns the same
 * guest). Stores the token in memory and `{hasSession, allowance}` in the
 * pointer — UNLESS a member token is present by the time the response
 * arrives (fix round 2: a cross-tab rebootstrap can sign this tab in while
 * the request is on the wire), in which case it throws `GuestSessionError`
 * ("already-member") and stores nothing — the orphaned guest row the server
 * just created is harmless; server-side retention removes it. Throws
 * `GuestSessionError` on any other failure too.
 *
 * Never sets `allowGuest` (auth is "none" per contract) and always sets
 * `skipRefreshOn401`, so this call can NEVER trigger `attemptGuestRefresh` —
 * load-bearing for `ensureGuestSession` below, which calls this from inside
 * `withGuestSessionLock`: `attemptGuestRefresh` takes that same lock, and the
 * lock is non-reentrant (see `session-lock.ts`). (Fix round 1, item 5: this
 * comment used to sit on `RawGuestSession` above instead of on this
 * function.)
 */
export async function createGuestSession(): Promise<GuestSessionResponse> {
  let raw: RawGuestSession | undefined;
  try {
    raw = await apiFetch<RawGuestSession | undefined, Record<string, never>>({
      method: "POST",
      path: AUTH_PATHS.guestCreate,
      body: {},
      skipRefreshOn401: true,
    });
  } catch (err) {
    throw toGuestSessionError(err);
  }
  const token = raw?.access_token;
  if (typeof token !== "string") {
    throw new GuestSessionError("guest.unavailable", null);
  }
  // Fix round 2: re-check AFTER the POST resolves, before storing anything —
  // the request just spent real time on the wire, and a member token can
  // have appeared during that window (a cross-tab rebootstrap). Never store a
  // guest token or touch the pointer once that's true.
  if (useAuthStore.getState().accessToken !== null) {
    throw new GuestSessionError("already-member", null);
  }
  const allowance = parseGuestAllowance(raw?.allowance);
  const expiresIn = raw?.expires_in;
  useAuthStore.getState().setGuestToken(token);
  updateGuestPointer({ hasSession: true, ...(allowance !== null ? { allowance } : {}) });
  return {
    access_token: token,
    token_type: "bearer",
    expires_in: typeof expiresIn === "number" ? expiresIn : 0,
    allowance,
  };
}

/** Contract name for the guest refresh (the implementation lives in api-client). */
export function refreshGuestSession(): Promise<RefreshOutcome> {
  return attemptGuestRefresh();
}

let inFlightEnsure: Promise<string> | null = null;

/**
 * The guest token, creating the session first if this tab has none. Never
 * on page load — called by the first send, the first mic use and the first
 * upload. Single-flight per tab; serialised across tabs.
 */
export function ensureGuestSession(): Promise<string> {
  // A signed-in tab must never mint a shadow guest (fix round 1, item 4).
  // This catches the common case — a member token already in memory when
  // this is FIRST called — with no request and without even touching
  // `inFlightEnsure`. It canNOT catch a member token that arrives LATER:
  // while this call is queued on the lock, or while `createGuestSession`'s
  // POST is in flight (fix round 2) — a cross-tab rebootstrap can finish in
  // either window. The lock callback below re-checks before it would skip or
  // send a request; `createGuestSession` re-checks again after its POST
  // resolves, before it stores anything — so a late-arriving member token
  // still never ends up with a planted guest token.
  if (useAuthStore.getState().accessToken !== null) {
    return Promise.reject(new GuestSessionError("already-member", null));
  }
  const existing = useAuthStore.getState().guestToken;
  if (existing !== null) return Promise.resolve(existing);
  if (inFlightEnsure !== null) return inFlightEnsure;
  inFlightEnsure = withGuestSessionLock(async () => {
    // Fix round 2: re-check here too — a member token can have appeared
    // while this call queued on the lock. Reject BEFORE returning a stale
    // cached guest token or sending a new create request.
    if (useAuthStore.getState().accessToken !== null) {
      throw new GuestSessionError("already-member", null);
    }
    // Another call in this tab may have finished while we waited; another
    // tab's create left a cookie, so the POST below returns that same guest.
    // `createGuestSession` never calls `attemptGuestRefresh` (see its doc
    // comment) — calling it here would deadlock this same lock.
    const now = useAuthStore.getState().guestToken;
    if (now !== null) return now;
    const session = await createGuestSession();
    return session.access_token;
  }).finally(() => {
    inFlightEnsure = null;
  });
  return inFlightEnsure;
}

export async function fetchGuestMe(): Promise<GuestMe> {
  const raw = await apiFetch<{ allowance?: unknown; thread_ids?: unknown } | undefined>({
    method: "GET",
    path: AUTH_PATHS.guestMe,
    allowGuest: true,
  });
  // Not an array at all (an empty/malformed 200 — apiFetch turns an empty
  // 2xx body into `undefined`) is UNKNOWN, never coerced to `[]`: `[]` would
  // read to `syncPointerFromMe` as "the server says zero threads" and drop a
  // real one (fix round 1, IMPORTANT). A real array still filters out any
  // non-string junk element, same as before.
  const listed: unknown = raw?.thread_ids;
  const ids = Array.isArray(listed)
    ? listed.filter((id): id is string => typeof id === "string")
    : null;
  return { allowance: parseGuestAllowance(raw?.allowance), thread_ids: ids };
}

/**
 * Fresh allowance into the pointer; a thread the server no longer lists is
 * dropped — but ONLY when the list is known-good. `me.thread_ids === null`
 * (fix round 1, IMPORTANT) means "unknown" (an empty/malformed body), not
 * "zero threads," so the drop is skipped and the pointer's thread survives.
 */
export function syncPointerFromMe(me: GuestMe): void {
  const threadId = readGuestPointer()?.threadId ?? null;
  const drop =
    me.thread_ids !== null && threadId !== null && !me.thread_ids.includes(threadId);
  updateGuestPointer({
    hasSession: true,
    ...(me.allowance !== null ? { allowance: me.allowance } : {}),
    ...(drop ? { threadId: null } : {}),
  });
}

interface RawGuestClaimResult {
  thread_ids?: unknown;
  active_thread_id?: unknown;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

/**
 * POST /auth/guest/claim — member Bearer + the `garden_gst` cookie. An empty
 * or malformed 200 (e.g. a misrouted base URL swallowing the real response)
 * must never hand the caller `undefined` typed as `GuestClaimResult` — the
 * body is validated against the contract shape (`thread_ids` a string array,
 * `active_thread_id` a string or exactly `null`); a mismatch throws
 * `GuestSessionError("guest.unavailable", …)` instead (fix round 1, item 3).
 * `GuestSessionError` is never an `ApiError`, so `classifyClaimFailure`
 * buckets it as `"retry"` automatically — never "gone"/"other-account" for a
 * response the server never actually sent.
 *
 * `signal` (optional, W2.6 fix round): the claim controller gives up on a
 * claim that never settles; the abort rejects with an `AbortError`, which
 * `classifyClaimFailure` also buckets as `"retry"`.
 */
export async function claimGuestSession(signal?: AbortSignal): Promise<GuestClaimResult> {
  const raw = await apiFetch<RawGuestClaimResult | undefined, Record<string, never>>({
    method: "POST",
    path: AUTH_PATHS.guestClaim,
    body: {},
    ...(signal !== undefined ? { signal } : {}),
  });
  const listed = raw?.thread_ids;
  if (!isStringArray(listed)) {
    throw new GuestSessionError("guest.unavailable", null);
  }
  const active = raw?.active_thread_id;
  if (active !== null && typeof active !== "string") {
    throw new GuestSessionError("guest.unavailable", null);
  }
  return { thread_ids: listed, active_thread_id: active };
}

/**
 * What a failed claim means (contract amendments B-3, B-4). Matched by the
 * CODE, not status alone (fix round 1, item 2) — a 404/409 from somewhere
 * other than this exact endpoint (a misrouted base URL hitting an SPA host's
 * catch-all 404, a proxy's own 409) falls to "retry" instead of being
 * mistaken for the contract's specific guest codes:
 *  - "gone"          404 `guest.no_session` — no guest cookie any more. After a
 *                    successful claim a SEQUENTIAL re-claim lands here too, so
 *                    the controller treats it as already handled (B-4). Also
 *                    403 `auth.registration_required` (ruling P9) — the caller
 *                    was never recognised as a member, which is terminal the
 *                    same way: clear the intent, never retry. Unreachable
 *                    through `claimGuestSession` above, which only ever sends
 *                    the member bearer (or none), but the classification must
 *                    not depend on that — a defensive 403 still lands here,
 *                    not in "retry".
 *  - "other-account" 409 `guest.claim.already_claimed` — another member owns it.
 *  - "not-allowed"   403 `guest.claim.not_allowed` (amendment B-10) — the member
 *                    holds admin-console access, and admins never claim. As
 *                    terminal as "other-account": retrying can only be refused
 *                    again, so the controller drops the conversation instead.
 *  - "retry"         503 `guest.unavailable` (Redis down, B-3), a malformed 200
 *                    (`claimGuestSession`'s own `GuestSessionError` — never an
 *                    `ApiError`, so it falls through to here automatically), a
 *                    code-less or differently-coded 404/409, any other status,
 *                    or no answer at all — keep the pointer and try again on
 *                    the next member-token or visibility event. Never discard
 *                    on "retry": the conversation is still claimable.
 */
export type GuestClaimFailure = "gone" | "other-account" | "not-allowed" | "retry";

export function classifyClaimFailure(err: unknown): GuestClaimFailure {
  if (err instanceof ApiError) {
    if (err.status === 404 && err.code === "guest.no_session") return "gone";
    if (err.status === 409 && err.code === "guest.claim.already_claimed") return "other-account";
    if (err.status === 403 && err.code === "guest.claim.not_allowed") return "not-allowed";
    if (err.status === 403 && err.code === "auth.registration_required") return "gone";
  }
  return "retry";
}

/**
 * The prefix of ChatRoute's thread-detail query keys (`threadQueryKey` there:
 * `["chat", "thread", id, limit]`) — every cached transcript. Note that it is
 * not a prefix of the thread LIST (`["chat", "threads"]`).
 */
export const THREAD_DETAIL_QUERY_KEY = ["chat", "thread"] as const;

/** ChatRoute's GET /auth/guest/me query (allowance + the guest's thread ids). */
export const GUEST_ME_QUERY_KEY = ["guest-me"] as const;

/**
 * An ended guest conversation leaves the app's QueryClient (final web fix
 * round, item 2): its cached transcripts and /auth/guest/me — see
 * `discardGuestSession` for why a cache entry outliving the conversation is
 * a leak.
 */
function forgetGuestConversationQueries(): void {
  queryClient.removeQueries({ queryKey: THREAD_DETAIL_QUERY_KEY });
  queryClient.removeQueries({ queryKey: GUEST_ME_QUERY_KEY });
}

/**
 * POST /auth/guest/discard — logout, "start over" or "don't save". The local
 * pointer, draft and token go FIRST and synchronously, so nothing waits on
 * the network (the claim hold and HomeRoute re-render at once). The POST is
 * best-effort: the server identifies the guest by the `garden_gst` cookie
 * (Bearer optional — contract), always answers 204 (B-3), and prunes an
 * abandoned guest after the retention window anyway.
 *
 * The conversation leaves this tab's query cache in the same synchronous
 * step (final web fix round, item 2): the cached transcripts and
 * /auth/guest/me. A disabled query still serves its cache entry and a fresh
 * one is not refetched, so «Начать заново» followed by Back to `/?t=<old>`
 * painted the discarded transcript — for the next guest in the tab too,
 * while the entry was fresh. Removed from the app's QueryClient singleton
 * (the one main.tsx provides), so every discard path is covered: «Начать
 * заново», «Не сохранять», logout and session end, the 409 claim outcome.
 */
export async function discardGuestSession(): Promise<void> {
  clearGuestPointer();
  clearGuestDraft();
  useAuthStore.getState().clearGuest();
  forgetGuestConversationQueries();
  try {
    await apiFetch<void, Record<string, never>>({
      method: "POST",
      path: AUTH_PATHS.guestDiscard,
      body: {},
      skipRefreshOn401: true,
    });
  } catch {
    // See above — nothing for the caller to do.
  }
}

/**
 * Re-bootstrap after this browser's guest session ended elsewhere — the one
 * path for both signals (spec edge case "Other tabs after a claim"): the
 * `storage` event from the claiming or discarding tab, and a `guest.claimed`
 * refusal (W2.4c), which the stream also returns instead of a 404 when a
 * claimed guest continues a thread that moved into the account (contract
 * amendment B-2). Runs the member refresh under the guest session lock, then
 * drops this tab's guest token: a claim means this browser signed in, so the
 * member cookie makes this tab the member too, and serialising stops N tabs
 * replaying one cookie. Never redirects — `attemptRefresh` only reports.
 *
 * MUST stay `attemptRefresh` (member) here, never `attemptGuestRefresh`:
 * `attemptGuestRefresh` acquires this same guest session lock, which is
 * non-reentrant, so calling it from inside this `withGuestSessionLock`
 * callback would deadlock (see `session-lock.ts` and the regression test in
 * `__tests__/api.test.tsx`). `attemptRefresh` (member) does not take this
 * lock, so it is safe here.
 *
 * The guest token goes only AFTER the member refresh has answered (final web
 * fix round, residual R1). It used to be cleared eagerly, before the lock —
 * and while the refresh was on the wire, a guest chat showing a thread it had
 * only REOPENED (`?t=`, never adopted) read "no token, no pointer, not a
 * member" as a stale link and started a new chat, so a claim in another tab
 * landed the member on an empty /chat instead of the claimed thread. Kept
 * until the answer, the token never leaves a gap: "refreshed" sets the member
 * token first, so no render sees neither identity. Then:
 *  - "refreshed" → cleared, whatever was planted meanwhile: if this tab's
 *    `ensureGuestSession()` held this same lock with its `POST /auth/guest`
 *    in flight, that create stores a fresh guest token once its own round-2
 *    check passes (`accessToken` is still null at that checkpoint — this
 *    refresh is queued behind the very lock the create holds, fix round 3),
 *    and a member tab must end with `guestToken: null` however the
 *    interleaving went;
 *  - "session-ended" / "unavailable" → only the ENDED session's token (the
 *    one in memory when this started) is cleared. A token a concurrent create
 *    planted belongs to a new guest session of this tab and stays, as before
 *    — there is no member session to protect against it.
 */
export async function rebootstrapAfterGuestEnded(): Promise<RefreshOutcome> {
  const endedGuestToken = useAuthStore.getState().guestToken;
  return await withGuestSessionLock(async () => {
    const outcome = await attemptRefresh();
    const auth = useAuthStore.getState();
    if (
      outcome === "refreshed" ||
      (endedGuestToken !== null && auth.guestToken === endedGuestToken)
    ) {
      auth.clearGuest();
    }
    return outcome;
  });
}

/**
 * Residual R2 (re-review of c8732dc1): a MEMBER tab whose browser-wide guest
 * conversation ended in another tab. This tab may hold that conversation:
 * the guest chatted here (turns adopted into the store, reads cached, an
 * unsent line in this tab's sessionStorage) and the member signed in here
 * with no claim intent, so the save prompt holds the chat. Once the pointer
 * goes, the hold ends — and without this the member saw the guest's local
 * turns (never fetched, `localThreadId` matched), the guest's cached read,
 * or the guest's unsent line in the composer.
 *
 * Runs in the storage event, BEFORE React renders the hold's end, so nothing
 * of the guest's is painted even for a commit — and whether or not this tab
 * is on the chat right now. This tab's OWN claim or «Не сохранять» never
 * fires a storage event here; those paths handle their own state.
 *  - The local copy goes, the thread stays: `localThreadId` and `localTurns`
 *    only, never `activeThreadId`/`?t=` and never `resetAll()` (whose
 *    reset-epoch path withholds the URL's thread). The member re-reads it:
 *    a claim shows the server's copy, a discard the not-found slot. A tab
 *    mid-turn is the member's own (a hold replaces the composer) and keeps
 *    its copy.
 *  - Every cached transcript is reset IN PLACE (`resetQueries`, not
 *    `removeQueries`): a mounted chat's query observer keeps its last data
 *    for `placeholderData`, and only an in-place reset empties that too.
 *    The member's own threads are simply read again.
 *  - The guest's unsent line goes: this tab cannot tell a claim from a
 *    discard, and the member chat would restore it into the composer.
 */
function releaseGuestConversationHeldByMember(): void {
  const chat = useChatStore.getState();
  if (chat.localThreadId !== null && !chat.isStreaming) {
    useChatStore.setState({ localThreadId: null, localTurns: [] });
  }
  clearGuestDraft();
  void queryClient.resetQueries({ queryKey: THREAD_DETAIL_QUERY_KEY });
}

/**
 * The `storage`-event handler (GuestClaimController). Another tab changed the
 * pointer:
 *  - the pointer still has a session → nothing now: this tab gets its token
 *    lazily (bootstrap or the next send), which keeps refreshes rare;
 *  - the session is gone and this tab is a member → release whatever of the
 *    guest conversation this tab held (`releaseGuestConversationHeldByMember`,
 *    residual R2) — the claim controller reacts to the pointer itself;
 *  - the session is gone and this tab is a guest → re-bootstrap, then reset
 *    the chat, drop the conversation from this tab's query cache and the
 *    guest's unsent line WHATEVER the outcome — the guest conversation is
 *    over in this tab: another tab claimed it, discarded it («Начать
 *    заново», «Не сохранять», logout) or saw its session end (final web fix
 *    round, item 2 and its re-review). "refreshed" is NOT proof of a claim:
 *    after «Не сохранять» this browser's member cookie belongs to the
 *    account that refused the conversation, and keeping the store showed the
 *    guest's local turns to that account (shared device); the member chat
 *    likewise restored the guest's unsent line into its composer (R2). After
 *    a real claim nothing is lost but that line — the member chat re-reads
 *    the claimed thread from the server. The reset runs AFTER the
 *    re-bootstrap: done first, the still-mounted guest chat would start a new
 *    chat while the refresh is on the wire and drop the `?t=` that takes the
 *    member to the claimed thread; done after, the member token has already
 *    swapped the guest chat out (HomeRoute), and the member chat mounts on
 *    the next navigation render with an empty store.
 */
export async function syncGuestSessionFromPointer(): Promise<RefreshOutcome | null> {
  if (readGuestPointer()?.hasSession === true) return null;
  if (useAuthStore.getState().accessToken !== null) {
    releaseGuestConversationHeldByMember();
    return null;
  }
  const outcome = await rebootstrapAfterGuestEnded();
  useChatStore.getState().resetAll();
  forgetGuestConversationQueries();
  clearGuestDraft();
  return outcome;
}

/** Test seam. */
export function __resetGuestApiForTests(): void {
  inFlightEnsure = null;
}
